"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Map, Building2, Leaf, Lightbulb, Sprout, Monitor, Scissors, ArrowRight } from "lucide-react";
const projects = [{
  id: 1,
  title: "Project Charter",
  category: "Initiation",
  description: "Foundational document defining scope, objectives, and stakeholders to ensure total alignment from day one.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1595871/img/deliverable_charter.png",
  colSpan: "md:col-span-8",
  icon: Map
}, {
  id: 2,
  title: "Risk Register",
  category: "Governance",
  description: "Proactive identification and mitigation strategies for every potential hurdle, updated throughout the lifecycle.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1595871/img/deliverable_risk.png",
  colSpan: "md:col-span-4",
  icon: Building2
}, {
  id: 3,
  title: "Gantt & Milestones",
  category: "Planning",
  description: "Visual timeline mapping critical paths, resource allocation, and firm delivery dates for absolute clarity.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1595871/img/deliverable_gantt.png",
  colSpan: "md:col-span-4",
  icon: Leaf
}, {
  id: 4,
  title: "Strategic Roadmap",
  category: "Consulting",
  description: "High-level visualization of programme objectives and the incremental value realized at every stage.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1595871/img/deliverable_roadmap.png",
  colSpan: "md:col-span-4",
  icon: Lightbulb
}, {
  id: 5,
  title: "Status Reports",
  category: "Execution",
  description: "Weekly executive summaries detailing progress, budget burn, and upcoming deliverables with no surprises.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1595871/img/deliverable_status.png",
  colSpan: "md:col-span-4",
  icon: Sprout
}, {
  id: 6,
  title: "ServiceNow Blueprint",
  category: "Implementation",
  description: "Technical architecture and migration path for seamless ServiceNow integration and platform scaling.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1595871/img/deliverable_servicenow.png",
  colSpan: "md:col-span-6",
  icon: Monitor
}, {
  id: 7,
  title: "Network Survey",
  category: "Field Services",
  description: "Detailed technical assessments of physical infrastructure and cabling requirements for Auckland sites.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1595871/img/deliverable_survey.png",
  colSpan: "md:col-span-6",
  icon: Scissors
}];
export default function PremiumPortfolioShowcase() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section data-section-id="146925" id="deliverablesshowcase" className="py-[120px] bg-slate-950 overflow-hidden !h-[2100px]" ref={ref}>
      <div className="container mx-auto px-4 md:px-6">
        <div className={`flex flex-col items-center text-center mb-16 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <span className="text-teal-400 font-sans font-semibold tracking-widest uppercase text-sm mb-4 block">
            Elite Precision
          </span>
          <h2 className="font-sans text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 max-w-3xl tracking-tight">
            What You Get. Every Time.
          </h2>
          <p className="font-sans text-slate-400 text-lg max-w-2xl">
            Outcome-based consulting means tangible results. We provide comprehensive documentation and strategic assets at every milestone, ensuring total transparency and project control.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16">
          {projects.map((project, i) => {
          const Icon = project.icon;
          return <div key={project.id} data-index={i} className={`relative group overflow-hidden rounded-[24px] h-[400px] md:h-[480px] ${project.colSpan} transition-all duration-1000 ease-out delay-[${i * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 saturate-50 brightness-75" />
                
                {}
                <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {}
                <div className="absolute bottom-6 left-6 right-6 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out bg-slate-900/90 backdrop-blur-xl border border-teal-500/30 rounded-2xl p-6 shadow-2xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-teal-500/10 rounded-full">
                      <Icon className="w-5 h-5 text-teal-400 stroke-[1.5]" />
                    </div>
                    <span className="text-xs font-semibold text-teal-400 tracking-wider uppercase font-sans">
                      {project.category}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2 font-sans">
                    {project.title}
                  </h3>
                  <p className="text-slate-300 text-sm font-sans line-clamp-2">
                    {project.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

        <div className={`flex justify-center transition-all duration-1000 ease-out delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Button asChild size="lg" className="rounded-full px-8 h-14 text-base font-sans bg-teal-500 hover:bg-teal-600 text-slate-950 border-none transition-colors">
            <Link to="/MilestoneMethodologyPage">
              Our Delivery Methodology
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}